import { AspectRatio } from "../components/common/DropDowns/StaticDropdown";
import { Status } from "../components/common/DropDowns/useStaticDropdown";
import { APPS, LABS } from "../constants/AppsConstants.ts";
import RouterConstants from "../constants/RouterConstants.ts";
import { IframeImageGenerationFoundationModels } from "../pages/IframeNew/IframeEditor/Form/ImageGeneration/IframeImageGenerationFoundationModels.tsx";
import { ImageEditingResetBehavior, ImageEditingViewMode } from "./image-to-image.ts";
import { Organization } from "./organization";

export enum IframePages {
	TEXT_TO_IMAGE = APPS.TEXT_TO_IMAGE,
	TEXT_TO_VECTOR = APPS.TEXT_TO_VECTOR,
	IMAGE_TO_IMAGE = APPS.IMAGE_TO_IMAGE,
	PRODUCT_PLACEMENT = APPS.PRODUCT_PLACEMENT,
	CAMPAIGN = APPS.CAMPAIGN,
	ADS_EDITOR = APPS.ADS_EDITOR,
	TG_TRAINING = "tgTraining",
	AUTOMOTIVE = APPS.AUTOMOTIVE,
	FIBO_LABS = LABS.FIBO,
	FIBO_EDIT = LABS.FIBO_EDIT,
}

export type ImageEditingAvailableTabs = {
	size_and_placement: boolean;
	single_background: boolean;
	bulk_background: boolean;
	objects: boolean;
	size: boolean;
	foreground_placement: boolean;
};
export type ImageEditingIframeConfig = {
	default_view_mode: ImageEditingViewMode;
	enable_bulk?: boolean;
	default_reset_behavior?: ImageEditingResetBehavior;
	available_tabs?: ImageEditingAvailableTabs;
};

export type ImageGenerationIframeConfig = {
	enabled_foundation_models?: IframeImageGenerationFoundationModels;
	enabled_tg_models?: string[];
	default_aspect_ratio?: AspectRatio;
	enable_structure_guidance?: boolean;
	enable_restyle_portraits?: boolean;
};

export type IframeCustomStyle = {
	logo?: {
		image?: string;
	};
	cssVars?: { [varKey: string]: string };
};

export type GeneralIframeConfig = {
	enable_close_window_btn?: boolean;
	enable_save?: boolean;
	enable_download?: boolean;
	enable_upload?: boolean;
	save_label?: string;
	enable_psd?: boolean;
	enable_increase_res?: boolean;
	enable_feedback_buttons?: boolean;
	user_notification_email?: string;
	content_moderation?: boolean;
};

export type AutomotiveConfig = {
	enable_project_management?: boolean;
	image_size: boolean;
	scenery: boolean;
	reflections: boolean;
	effects: boolean;
	harmonization: boolean;
	modify_objects: boolean;
	enable_bulk: boolean;
	default_view_mode?: ImageEditingViewMode;
};

export type TgTrainingConfig = {
	enabled_training_models?: { [key in AvailableTrainingModels]: boolean };
};

export type AvailableTrainingModels = "expert" | "fully_automated";

export type IframeConfig = {
	allowed_domains: string[];
	enabled_pages: Partial<{ [page in IframePages]: boolean }>;
	landing_page: IframePages;
	custom_style?: IframeCustomStyle;
	general_config?: GeneralIframeConfig;
	image_generation_config?: ImageGenerationIframeConfig;
	image_editing_config?: ImageEditingIframeConfig;
	automotive_config?: AutomotiveConfig;
	tg_training_config?: TgTrainingConfig;
};

export type Iframe = {
	id: string;
	name: string;
	description: string;
	status: Status;
	config: IframeConfig;
	org_id?: string;
	created_at: Date;
	updated_at: Date;
	organization: Organization;
	username: string;
	password: string;
};

export const getDefaultIframe = (orgId: string) =>
	({
		id: "",
		name: "Untitled - Iframe Name",
		description: "",
		status: "Pending",
		config: {
			allowed_domains: [],
			enabled_pages: {
				[IframePages.TEXT_TO_IMAGE]: true,
				[IframePages.TEXT_TO_VECTOR]: false,
				[IframePages.IMAGE_TO_IMAGE]: true,
				[IframePages.AUTOMOTIVE]: false,
				[IframePages.TG_TRAINING]: false,
			},
			landing_page: IframePages.TEXT_TO_IMAGE,
			custom_style: {
				cssVars: {
					primary_main_color: "#8800FF",
				},
			},
			general_config: {
				enable_close_window_btn: false,
				enable_download: true,
				enable_save: true,
				enable_upload: true,
				enable_psd: false,
				enable_increase_res: false,
				enable_feedback_buttons: true,
				content_moderation: false,
			},
			image_generation_config: {
				enable_structure_guidance: true,
				enable_restyle_portraits: false,
			},
			image_editing_config: {
				default_view_mode: ImageEditingViewMode.GALLERY,
				enable_bulk: true,
				default_reset_behavior: ImageEditingResetBehavior.CLEAR,
				available_tabs: {
					size: true,
					size_and_placement: true,
					single_background: true,
					bulk_background: true,
					objects: true,
					foreground_placement: true,
				},
			},
			automotive_config: {
				enable_project_management: false,
				image_size: false,
				scenery: false,
				reflections: false,
				effects: false,
				harmonization: false,
				modify_objects: false,
				enable_bulk: false,
				default_view_mode: ImageEditingViewMode.SINGLE,
			},
			tg_training_config: {
				enabled_training_models: {
					fully_automated: true,
					expert: true,
				},
			},
		},
		created_at: new Date(),
		updated_at: new Date(),
		organization: {
			id: orgId,
			allowed_pages: {
				[IframePages.TEXT_TO_IMAGE]: true,
				[IframePages.TEXT_TO_VECTOR]: false,
				[IframePages.IMAGE_TO_IMAGE]: true,
				[IframePages.AUTOMOTIVE]: true,
				[IframePages.TG_TRAINING]: true,
			},
			is_blocked: false,
		},
		org_id: orgId,
		username: "",
		password: "",
	}) as Iframe;

export enum IframePostMessageTypes {
	InitPreview = "Bria_InitPreview",
	InitialLoadComplete = "Bria_InitialLoadComplete",
	CloseClicked = "Bria_CloseClicked",
	Navigate = "Bria_Navigate",
	Action = "Bria_Action",
	AuthFailed = "Bria_AuthFailed",
}

export enum IframePostMessageActionTypes {
	LogoClick = "logo_click",
	Upload = "upload",
	Generate = "generate",
	EraseObject = "erase_object",
	GenerativeFill = "generative_fill",
	Download = "download",
	Save = "save",
}

export enum IframePostMessageItemTypes {
	Template = "template",
	Image = "image",
	Psd = "psd",
}

export const iframeRoutes: Partial<{ [landingPage in IframePages]: string }> = {
	[IframePages.ADS_EDITOR]: `${RouterConstants.ADS_EDITOR.fullPath}`,
	[IframePages.TEXT_TO_IMAGE]: `${RouterConstants.TEXT_TO_IMAGE.fullPath}`,
	[IframePages.TEXT_TO_VECTOR]: `${RouterConstants.TEXT_TO_VECTOR.fullPath}`,
	[IframePages.IMAGE_TO_IMAGE]: `${RouterConstants.IMAGE_TO_IMAGE.fullPath}`,
	[IframePages.AUTOMOTIVE]: `${RouterConstants.AUTOMOTIVE.path}`,
	[IframePages.CAMPAIGN]: `${RouterConstants.ADS_GENERATOR.fullPath}`,
	[IframePages.TG_TRAINING]: `${RouterConstants.NEW_TG.PROJECTS.fullPath}`,
	[IframePages.FIBO_LABS]: `${RouterConstants.LABS.FIBO.fullPath}`,
	[IframePages.FIBO_EDIT]: `${RouterConstants.LABS.FIBO_EDIT.fullPath}`,
};
